<template>
  <div class="app-filter" v-if="!isLoading">
    <v-row class="filters-row">
      <div class="filter-group">
        <!-- Bulk Select -->

        <div class="bulk-select" :class="{ 'active-bulk': !!isBulk }" v-if="!hideBulk">
          <BulkSelect :handleOnSelectAll="handleOnSelectAll">
            <template v-slot:bulk-edit>
              <slot name="bulk-edit" />
            </template>
          </BulkSelect>
        </div>

        <div class="normal-filter" :class="{ 'd-none': !!isBulk }">
          <!-- Filter Box -->
          <slot name="filter-btn" v-if="isFilterBtn">
            <FilterBox :handleOnOpenFilter="handleOnOpenFilter" tabindex="1" v-if="isColumnFilter" />
          </slot>

          <!-- Filter Location -->
          <slot name="location">
            <component :is="locationFilterType" :isLocations="isLocations" tabindex="2" v-if="isLocations" />
            <div class="locations-filter-menu-list" id="locations_filter_menu_list"></div>
          </slot>

          <!-- Filter Status -->
          <slot name="status"></slot>

          <!-- Filter Search -->
          <slot name="search" v-if="!hideSearch">
            <component
              :is="filterSearchType"
              :is-nested-search="isNestedSearch"
              tabindex="3"
              :placeholder="searchPlaceholder"
              :columns="columns"
              :textSuffix="textSuffix"
            />
          </slot>
        </div>
      </div>

      <v-spacer />

      <!-- Right Side Filters -->
      <div class="filter-actions">
        <slot>
          <FilterActions tabindex="4" />
        </slot>
      </div>
    </v-row>
    <!-- <v-row class="filters-row-footer">
      <div class="filter-drop-down-list">
        <div class="filter-list --drop-down" :class="show && 'active'" v-handle-click-outside="handleOnOutSideClick">
          <AppFiltersOptionsList v-if="isOptionsFilter" />
        </div>
      </div>
    </v-row> -->
    <AppDrawer :isVisible="show" :handleOnClose="handleOnOutSideClick" :width="'auto'">
      <slot name="advanced-filter">
        <FilterForm :columns="advancedColumns" :options="options"> </FilterForm>
      </slot>
    </AppDrawer>
  </div>
</template>

<script>
import BasicFilterSearch from "./search/basic-search";
import AdvancedFilterSearch from "./search/advanced-search";
import FilterBox from "./filter";
import SingleLocationFilter from "./locations/single";
import MultipleLocationFilter from "./locations/multiple";
import FilterButtonsView from "./actions";
import AppFiltersOptionsList from "./options/";
import { mapGetters, mapMutations } from "vuex";
import { Filter } from "@/modules/app/filter";
import Vue from "vue";
import AgentOrdersList from "@/views/call-center/orders/list/tab/view/agent";
import BulkSelect from "./select/index";
import FilterForm from "./filter/form";
import Component from "../messages/toast/component.vue";

export default Vue.extend({
  name: "AppFilter",

  components: {
    AgentOrdersList,
    SingleLocationFilter,
    MultipleLocationFilter,
    FilterBox,
    BasicFilterSearch,
    FilterButtonsView,
    AppFiltersOptionsList,
    BulkSelect,
    FilterForm,
    AdvancedFilterSearch,
  },

  data() {
    return {
      show: false,
      isLoading: true,
      isBulk: false,
      locationFilterType: MultipleLocationFilter,
      filterSearchType: BasicFilterSearch,
    };
  },

  props: {
    isLocations: {
      type: Boolean,
      default: true,
    },

    columns: {
      type: [Array, null],
      default: null,
    },

    advancedOptions: {
      type: [Array, null],
      default: null,
    },

    model: {
      type: String,
      default: "model_unknown",
    },

    isSingleLocation: {
      type: Boolean,
      default: false,
    },

    isFilterBtn: {
      type: Boolean,
      default: false,
    },

    searchPlaceholder: {
      type: String,
      default: "",
    },

    handleOnSelectAll: {
      type: Function,
      default: () => {},
    },

    hideBulk: {
      type: Boolean,
      default: false,
    },
    isNestedSearch: {
      type: Boolean,
      default: false,
    },

    hideSearch: {
      type: Boolean,
      default: false,
    },

    isAdvancedSearch: {
      type: Boolean,
      default: false,
    },

    textSuffix: {
      type: String,
      default: "is",
    },

    options: {
      type: [Array, null],
      default: null,
    },
  },

  created() {
    this.prepareFilter();

    if (!!this.isSingleLocation) {
      this.locationFilterType = SingleLocationFilter;
    }
    if (this.isAdvancedSearch) {
      this.filterSearchType = AdvancedFilterSearch;
    }
  },

  watch: {
    "filter.isBulk": {
      immediate: true,
      deep: true,
      handler(isBulk) {
        this.isBulk = isBulk;
      },
    },
  },

  computed: {
    ...mapGetters({
      filter: "App/filter",
    }),

    /**
     * Is column filters
     * @return {Boolean}
     */
    isColumnFilter() {
      return !!this.filter?.columns?.length;
    },
    /**
     * columns with advanced filter enabled
     */
    advancedColumns() {
      return this.filter?.advancedFilter.columns;
    },

    /**
     * Is options filters
     * @return {Boolean}
     */
    isOptionsFilter() {
      return true; //!!this.filter?.options?.length;
    },
  },

  methods: {
    ...mapMutations({
      setFilter: "App/SET_FILTER",
    }),

    /**
     * Prepare filter Object
     * @return {void}
     */
    prepareFilter() {
      const filter = new Filter({
        columns: this?.columns || [],
        name: this.model,
        isLocations: this.isLocations,
        isAdvanced: this.isFilterBtn,
        advancedOptions: this?.advancedOptions,
      });

      this.setFilter(filter);
      this.isLoading = false;
    },

    /**
     * Handle toggle show tags
     */
    toggle(bool = false) {
      this.show = bool;
      this.$forceUpdate();
    },

    /**
     * Open filters list
     * @return {void | *}
     */
    handleOnOpenFilter() {
      if (this.show) {
        return this.handleOnOutSideClick();
      }

      this.toggle(true);
    },

    /**
     * Close filter
     * @return {void}
     */
    handleOnCloseFilter() {
      this.advancedColumns.forEach((column) => {
        if (!column.value || column.value?.length === 0) column.closWidget();
      });
      this.toggle();
    },

    /**
     * Close search & filters list if user clicked outside
     * @return {void | *}
     */
    handleOnOutSideClick() {
      this.$nextTick(() => {
        if (!this.show) {
          return false;
        }

        this.handleOnCloseFilter();
      });
    },
  },

  directives: {
    /**
     * directive to handel user click out of the auto complete box
     * @directive
     */
    "handle-click-outside": {
      bind: async (el, binding) => {
        const { bubble } = binding.modifiers;
        const clickHandler = (e) => {
          if (bubble || (!el.contains(e.target) && el !== e.target) || e.target.id === "filter-btn") {
            binding.value(e);
          }
        };
        el.__vueHandleClickOutside__ = clickHandler;
        document.addEventListener("click", clickHandler, true);
      },
      unbind: function (el) {
        document.removeEventListener("click", el?.__vueHandleClickOutside__);
        el.__vueHandleClickOutside__ = null;
      },
    },
  },
});
</script>

<style scoped src="./styles.scss" lang="scss"></style>
